import { useState } from 'react';
import { Card, Tag, Typography, Tooltip, Button } from 'antd';
import { CheckCircleFilled, WarningFilled, HolderOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { EntityImage } from '../Entities/EntityImage';
import SeatRing from './SeatRing';
import { describeRoom, topicFor } from '../../utils/gathering';
import { isGuestPerson } from '../../utils/guest_session';

const { Text } = Typography;

export const TRAY = -1;

/**
 * The rooms. Each card shows its table (SeatRing) and its people; a person's
 * left edge is the colour of the seat they fill (neutral = open seat), their
 * dots are every group they picked. A room's topic is topicFor(topicOffset + r) — topics carry on across rounds. Drag a person onto another room — or to
 * or from the "Not in a room" tray above, which collects anyone in the pool
 * the rooms don't include yet (e.g. people who joined after publishing).
 * Seat labels and the met/short badge are re-derived from the new line-up
 * (describeRoom), never patched.
 */
function RoomBoard({ rooms, unseated, groups, colorOf, membersOf, peopleById, topics = [], topicOffset = 0, highlight, onMove, onJoinRoom }) {
    const [dragging, setDragging] = useState(null); // { personId, from }
    const [over, setOver] = useState(null);
    const nameOf = gid => groups.find(g => g.id === gid)?.name || 'Untitled';

    const dropProps = target => ({
        onDragOver: e => { if (dragging) { e.preventDefault(); setOver(target); } },
        onDragLeave: () => setOver(o => (o === target ? null : o)),
        onDrop: e => {
            e.preventDefault();
            if (dragging && dragging.from !== target) onMove(dragging.personId, dragging.from, target);
            setDragging(null);
            setOver(null);
        },
    });
    const isOver = target => over === target && dragging && dragging.from !== target;

    const chip = (id, from, seatGroup) => {
        const person = peopleById.get(id);
        const inGroups = groups.filter(g => membersOf.get(g.id)?.has(id));
        const dim = highlight && !membersOf.get(highlight)?.has(id);
        return (
            <div
                key={id}
                draggable
                onDragStart={e => {
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox won't start a drag without data.
                    e.dataTransfer.setData('text/plain', String(id));
                    // Defer: setDragging mounts the tray above the rooms, and
                    // moving the dragged chip during dragstart makes Chrome
                    // cancel the drag on the spot.
                    setTimeout(() => setDragging({ personId: id, from }), 0);
                }}
                onDragEnd={() => { setDragging(null); setOver(null); }}
                className="gathering-person"
                style={{
                    borderLeftColor: seatGroup ? colorOf(seatGroup) : 'var(--border-color)',
                    opacity: dim ? 0.3 : 1,
                }}
            >
                <HolderOutlined className="gathering-person-grip" />
                {person && <EntityImage entity={person} size={20} />}
                <Text ellipsis className="gathering-person-name">{person?.name ?? `#${id}`}</Text>
                {isGuestPerson(person) && <Tag color="gold" className="gathering-guest-tag">Guest</Tag>}
                <span className="gathering-person-dots">
                    {inGroups.map(g => (
                        <Tooltip key={g.id} title={g.name || 'Untitled'}>
                            <span className="gathering-dot gathering-dot-sm" style={{ background: colorOf(g.id) }} />
                        </Tooltip>
                    ))}
                </span>
            </div>
        );
    };

    return (
        <>
            {(unseated.length > 0 || dragging) && (
                <div className={`gathering-tray${isOver(TRAY) ? ' is-over' : ''}`} {...dropProps(TRAY)}>
                    <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                        Not in a room{unseated.length ? ` · ${unseated.length}` : ''} — drag into a room
                    </Text>
                    <div className="gathering-tray-people">
                        {unseated.map(id => chip(id, TRAY, null))}
                    </div>
                </div>
            )}
            <div className="gathering-rooms">
                {rooms.map((room, r) => {
                    const { seats, mix, met } = describeRoom(room, groups, membersOf);
                    const short = mix.filter(m => m.seated < m.target);
                    const ring = [
                        ...room.personIds.map(id => ({
                            color: seats[id] ? colorOf(seats[id]) : 'var(--border-color-strong)',
                            state: 'filled',
                            label: `${peopleById.get(id)?.name ?? 'Someone'} — ${seats[id] ? `${nameOf(seats[id])} seat` : 'open seat'}`,
                        })),
                        ...short.flatMap(m => Array.from({ length: m.target - m.seated }, () => ({
                            color: colorOf(m.groupId), state: 'missing', label: `Unfilled ${nameOf(m.groupId)} seat`,
                        }))),
                    ];
                    // Order by seat, so a card reads as its mix.
                    const order = groups.map(g => g.id).concat([null]);
                    const members = [...room.personIds].sort((a, b) => order.indexOf(seats[a]) - order.indexOf(seats[b]));

                    return (
                        <Card
                            key={r}
                            size="small"
                            className={`gathering-room${isOver(r) ? ' is-over' : ''}`}
                            {...dropProps(r)}
                            title={(
                                <div className="gathering-room-title">
                                    <span>Room {r + 1}</span>
                                    <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 'var(--fs-sm)' }}>
                                        {room.personIds.length} {room.personIds.length === 1 ? 'person' : 'people'}
                                    </Text>
                                </div>
                            )}
                            extra={(
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {room.hangoutRoomName && onJoinRoom && (
                                        <Button size="small" icon={<VideoCameraOutlined />} onClick={() => onJoinRoom(room, r)}>
                                            Join
                                        </Button>
                                    )}
                                    {met
                                        ? <Tooltip title="Every reserved seat is filled"><CheckCircleFilled style={{ color: 'var(--status-success)' }} /></Tooltip>
                                        : (
                                            <Tooltip title={short.map(m => `${nameOf(m.groupId)}: ${m.seated}/${m.target}`).join(' · ')}>
                                                <WarningFilled style={{ color: 'var(--status-warning)' }} />
                                            </Tooltip>
                                        )}
                                </span>
                            )}
                        >
                            <div className="gathering-room-body">
                                {topicFor(topicOffset + r, topics) && (
                                    <Text className="gathering-room-topic">{topicFor(topicOffset + r, topics)}</Text>
                                )}
                                <div className="gathering-room-ring">
                                    <SeatRing seats={ring} size={84} />
                                    <div className="gathering-room-mix">
                                        {mix.filter(m => m.target > 0 || m.present > 0).map(m => (
                                            <Tooltip
                                                key={m.groupId}
                                                title={`${nameOf(m.groupId)}: ${m.present} in the room, ${m.seated} of ${m.target} reserved seats filled`}
                                            >
                                                <Tag variant="filled" className="gathering-mix-tag" style={{ color: colorOf(m.groupId) }}>
                                                    <span className="gathering-dot" style={{ background: colorOf(m.groupId) }} />
                                                    {m.present}{m.target > 0 ? `/${m.target}` : ''}
                                                </Tag>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                                <div className="gathering-room-people">
                                    {members.map(id => chip(id, r, seats[id]))}
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </>
    );
}

export default RoomBoard;
